import { Form, FormMessage } from "@/components/ui/form";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { MezzaFormField } from "@/components/core/Form/MezzaFormField";
import { LocationMultiSelect } from "@/components/core/Form/LocationMultiSelect";
import { PhotoFormField } from "@/components/core/Form/PhotoFormField";
import { useState, useMemo, useEffect } from "react";
import { MAX_FILE_SIZE } from "@/utils/file.utils";
import { useSupportRequestMutation } from "@/stores/queries/support.query";
import { ButtonWithLoading } from "@/components/core/Form/ButtonWithLoading";
import { useActiveLocationsStore } from "@/stores/useActiveLocationsStore";

interface ReportIssueFormProps {
  initialState?: {
    transactionId?: string;
    description?: string;
  };
}

export function ReportIssueForm({ initialState }: ReportIssueFormProps) {
  const { t } = useTranslation();
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const { submitSupportRequest, isPending } = useSupportRequestMutation();
  const { availableLocations } = useActiveLocationsStore();

  // Auto-select single location
  const defaultLocationIds = useMemo(() => {
    return availableLocations.length === 1 ? [availableLocations[0].locationId] : [];
  }, [availableLocations]);

  // Define schema inside component to access translation function
  const reportIssueSchema = z.object({
    locationIds: z
      .array(z.string())
      .min(1, t("support.validation.locationsRequired")),
    transactionId: z.string().optional(),
    description: z
      .string()
      .min(1, t("errors.required"))
      .min(10, t("validation.minLength", { count: 10 }))
      .max(1000, t("errors.maxLength", { max: 1000 })),
    attachments: z
      .instanceof(File)
      .optional()
      .refine(
        (file) => {
          if (!file) return true;
          return file.size && file.size <= MAX_FILE_SIZE;
        },
        {
          message: t("errors.fileSize"),
        }
      ),
  });

  type ReportIssueFormData = z.infer<typeof reportIssueSchema>;

  const form = useForm<ReportIssueFormData>({
    resolver: zodResolver(reportIssueSchema),
    defaultValues: {
      locationIds: defaultLocationIds,
      transactionId: initialState?.transactionId || "",
      description: initialState?.description || "",
    },
    mode: "all",
  });

  useEffect(() => {
    return () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview);
    };    
  }, [photoPreview]);

  const handlePhotoSelect = (file: File) => {
    if (file.size > MAX_FILE_SIZE) {
      form.setError("attachments", { message: t("errors.fileSize") });
      return;
    }

    form.setValue("attachments", file);
    form.clearErrors("attachments");
    setPhotoPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  };

  const handlePhotoRemove = () => {
    form.setValue("attachments", undefined);
    setPhotoPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const onSubmit = async (data: ReportIssueFormData) => {
    await submitSupportRequest(data);
    form.reset();
    setPhotoPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4 rounded-lg border p-4"
      >
        <LocationMultiSelect
          form={form}
          name="locationIds"
          label="support.locations"
          disabled={isPending}
        />

        <MezzaFormField
          form={form}
          name="transactionId"
          label="orders.orderId"
          placeholder="common.placeholder.orderId"
        />

        <MezzaFormField
          form={form}
          name="description"
          label="common.comment"
          placeholder="common.placeholder.comment"
          type="textarea"
          className="min-h-[100px]"
        />

        <PhotoFormField
          onPhotoSelect={handlePhotoSelect}
          onPhotoRemove={handlePhotoRemove}
          photoPreview={photoPreview}
        />

        {form.formState.errors.attachments && (
          <FormMessage>{form.formState.errors.attachments.message}</FormMessage>
        )}

        <ButtonWithLoading
          type="submit"
          className="w-full"
          disabled={form.formState.isSubmitting || isPending}
          isLoading={form.formState.isSubmitting || isPending}
        >
          {t("common.submit")}
        </ButtonWithLoading>
      </form>
    </Form>
  );
}
